<script setup lang="ts">
import { NButton, NDropdown, useDialog, useMessage } from 'naive-ui'
import { h, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { SvgIcon } from '@/components/common'
import { t } from '@/locales'
import { useAppStore } from '@/store'
import { useBasicLayout } from '@/hooks/useBasicLayout'
import FormCommon from '@/views/folder/FormCommon.vue'
import { useWorkspaceFolderStore } from '@/store/modules/folder'
import type { WorkspaceFolderDetail } from '@/typings/workspaceFolder'
import { getListAgentByFolder } from '@/api/agent'

const props = defineProps({
  isOrder: {
    type: Boolean,
    default: false,
  },
  uuid: {
    type: Number,
    default: null,
  },
  showDelete: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['dropdown-change'])
const route = useRoute()
const router = useRouter()
const { isMobile } = useBasicLayout()
const message = useMessage()
const appStore = useAppStore()
const workspaceFolderStore = useWorkspaceFolderStore()
const dialog = useDialog()

const isShowModal = ref<boolean>(false)
const rowData = ref<Record<string, any>>({})
const isDropdownOpen = ref<boolean>(false)
const listAgent = ref<any[]>([])

const handleDropdownChange = (show: boolean) => {
  isDropdownOpen.value = show
  emit('dropdown-change', show)
}

const renderIcon = (prop: any) => {
  return () => h(SvgIcon, prop)
}
const menuOptions = ref([
  { label: t('common.rename'), icon: renderIcon({ icon: 'ion:create-outline' }), key: 'edit' },
  { label: t('common.pin'), icon: renderIcon({ icon: 'ion:pricetag-outline' }), key: 'pin' },
  {
    label: () =>
      h('span', { class: 'text-red-500' }, t('common.delete')),
    icon: renderIcon({ icon: 'ion:trash-outline', class: 'text-red-500' }),
    key: 'delete',
    show: props.showDelete,
  },
])

function handleEdit(item: WorkspaceFolderDetail) {
  rowData.value = { ...item }
  isShowModal.value = true
}

const handlePin = () => {
  if (props.isOrder)
    workspaceFolderStore.unpinFolder(props.uuid)
  else
    workspaceFolderStore.pinFolder(props.uuid)
}

const onCancelDelete = () => {
  dialog.destroyAll()
}

const onDelete = async () => {
  try {
    const success = await workspaceFolderStore.removeFolder(props.uuid)
    if (success) {
      if (isMobile.value)
        appStore.setSiderCollapsed(true)
      dialog.destroyAll()
      message.success('Xóa thành công')

      // Kiểm tra nếu đang ở trang folder bị xóa, redirect về agent-management
      const currentFolderId = route.params.folderId
      if (currentFolderId && Number(currentFolderId) === props.uuid)
        router.push({ name: 'dashboard' })
    }
  }
  catch (error) {
    console.error('Failed to delete folder:', error)
    dialog.destroyAll()
  }
}

const handleDelete = (index: number) => {
  dialog.warning({
    title: t('folder.titleDelete'),
    showIcon: false,
    autoFocus: false,
    maskClosable: false,
    closable: false,
    class: '!rounded-2xl !max-w-[368px]',
    content: () =>
      h('div', { }, [
        listAgent.value.length
          ? h('div', { class: 'mb-2' }, [
					 h('span', { }, t('folder.contentDeleteTopLeft')),
					 h('span', { class: 'font-bold' }, `${listAgent.value.length} agents`),
            h('span', { }, t('folder.contentDeleteTopRight')),

          ])
          : null,
        h('div', {}, t('folder.contentDeleteBottom')),
      ],
      ),
    action: () =>
      h(
        'div',
        { class: 'grid grid-cols-1 sm:grid-cols-2 gap-3 w-full' },
        [
          h(NButton, { ghost: true, type: 'primary', class: 'min-w-32 !w-full', onClick: () => onCancelDelete() }, t('common.cancel')),
          h(NButton, { type: 'primary', class: 'min-w-32 !w-full', onClick: () => onDelete() }, t('common.delete')),
        ],
      ),
  })
}

const handleSelectDropdown = (key: string, folder: WorkspaceFolderDetail, index: number) => {
  switch (key) {
    case 'edit':
      handleEdit(folder)
      break
    case 'pin':
      handlePin()
      break
    case 'delete':
      handleDelete(index)
      break
  }
}

const handleMenuOptions = (options: any, isOrder: boolean) => {
  return options.map((element: any) => {
    return {
      ...element, label: (element.key === 'pin' && isOrder) ? t('common.unpin') : element.label,
    }
  })
}

function onClose(reload: boolean) {
  isShowModal.value = false
}

const fetchAgents = async () => {
  if (!props.uuid)
    return

  try {
    const response = await getListAgentByFolder({
      workspace_folder_id: props.uuid,
      page: 1,
      page_size: 100,
    })
    if (response?.data?.items)
      listAgent.value = response.data.items
  }
  catch (error) {
  }
  finally {
  }
}

onMounted(() => {
  fetchAgents()
})
</script>

<template>
  <NDropdown
    trigger="click"
    :options="handleMenuOptions(menuOptions, isOrder)"
    placement="left-start"
    @select="(key: string) => handleSelectDropdown(key, workspaceFolderStore.getFolders.find(f => f.id === uuid)!, workspaceFolderStore.getFolders.findIndex(f => f.id === uuid))"
    @update:show="handleDropdownChange"
  >
    <NButton
      size="tiny" secondary circle style="background-color: transparent; border-color: gray;border-width: 1px;border-style: solid;"
    >
      <SvgIcon icon="ion:ellipsis-vertical-outline" size="14" />
    </NButton>
  </NDropdown>
  <FormCommon
    v-if="isShowModal"
    :data-source="rowData"
    :show="isShowModal"
    :is-view="false"
    :is-edit="true"
    :is-create="false"
    title="Đổi tên thư mục"
    @on-close="onClose"
  />
</template>

<style scoped>
</style>
